'use strict';

var defaults = require('../defaults/Defaults');
var KayleeBase = require('./KayleeBase');
var KayleeEquipmentTypes = require('./KayleeEquipmentTypes');
var log = require('../log/Log').channel('KayleeEventFormatter');
var UaParser = require('ua-parser-js');

var uaParser = new UaParser();

var KayleeEventFormatter = {
    getActor: getActor,
    getDescription: getDescription,
};

var formatInfo = {
    billingAccountRole: {
        $addName: function (desc, event, format) {
            var roles = {
                admin: 'Administrator permissions',
                doer: 'Purchasing permissions',
                viewer: 'Reporting permissions',
            };

            desc.push(roles[event.role]);
        },
        changed: function (desc, event, format) {
            var roles = {
                admin: 'Administrator',
                doer: 'Purchasing',
                viewer: 'Reporting',
            };

            desc.push('changed to ');
            desc.push(roles[event.changed.role]);
            desc.push(' for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        created: function (desc, event, format) {
            desc.push('added for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        deleted: function (desc, event, format) {
            desc.push('deleted for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
    },
    company: {
        $addName: function (desc, event, format) {
            addCompanyName(desc, event, true, format);
        },
        changed: {
            name: {
                $altProp: 'companyName',
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('renamed to ');
                    desc.push(escape(changed, format));
                },
            },
            settings: {
                timeZone: { $displayName: 'time zone' },
                office365Enabled: {
                    $trueDescription: 'Office 365 integration enabled',
                    $falseDescription: 'Office 365 integration disabled',
                },
                office365Domain: { $displayName: 'Office 365 domain' },
            },
        },
        created: 'created',
        deleted: 'deleted',
    },
    controller: {
        $addName: function (desc, event, format, formatOptions) {
            addControllerName(desc, event, true, format, formatOptions);
        },
        changed: {
            appVersion: { $displayName: 'application version' },
            dbConnected: {
                $trueDescription: 'connected to ' + defaults.mal.name,
                $falseDescription: 'disconnected from ' + defaults.mal.name,
            },
            isCharging: {
                $trueDescription: 'connected to power',
                $falseDescription: 'disconnected from power',
            },
            platformVersion: { $displayName: 'platform version' },
        },
        created: function (desc, event, format) {
            desc.push('registered with ');
            desc.push(defaults.mal.name);
        },
        paired: function (desc, event, format) {
            desc.push('paired with ');
            addRoomName(desc, event, false, format);
        },
        unpaired: function (desc, event, format) {
            desc.push('unpaired from ');
            addRoomName(desc, event, false, format);
        },
    },
    distributorRole: {
        $addName: function (desc, event, format) {
            var roles = {
                admin: 'Administrator permissions',
                doer: 'Purchasing permissions',
                viewer: 'Reporting permissions',
            };

            desc.push(roles[event.role]);
        },
        changed: function (desc, event, format) {
            var roles = {
                admin: 'Administrator',
                doer: 'Purchasing',
                viewer: 'Reporting',
            };

            desc.push('changed to ');
            desc.push(roles[event.changed.role]);
            desc.push(' for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        created: function (desc, event, format) {
            desc.push('added for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        deleted: function (desc, event, format) {
            desc.push('deleted for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
    },
    equipment: {
        $addName: function (desc, event, format) {
            addEquipmentName(desc, event, false, format);
        },
        changed: {
            config: {
                airPlayContentEnabled: {
                    $trueDescription: 'AirPlay content sharing enabled',
                    $falseDescription: 'AirPlay content sharing disabled',
                },
                alfatronControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                atdm0604ControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                audioDialingEnabled: {
                    $trueDescription: 'audio dialing support enabled',
                    $falseDescription: 'audio dialing support disabled',
                },
                autoSwitchVideoInputs: {
                    $trueDescription: 'auto switch video inputs enabled',
                    $falseDescription: 'auto switch video inputs disabled',
                },
                callParticipantsLimit: { $displayName: 'call participants limit' },
                cameraPresets: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('camera presets configuration changed');
                    },
                },
                cameraTrackingUiEnabled: {
                    $trueDescription: 'camera tracking UI enabled',
                    $falseDescription: 'camera tracking UI disabled',
                },
                controlSpaceControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                convergeProControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                convergePro2ControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                deviceId: { $displayName: 'device ID' },
                displayBlankIndex: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('display blank ordering changed');
                    },
                },
                displayBlankUiEnabled: {
                    $trueDescription: 'display blank UI enabled',
                    $falseDescription: 'display blank UI disabled',
                },
                displayFrerezeIndex: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('display freeze ordering changed');
                    },
                },
                displayFreezeUiEnabled: {
                    $trueDescription: 'display freeze UI enabled',
                    $falseDescription: 'display freeze UI disabled',
                },
                displayMotion: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('display motion controls changed');
                    },
                },
                dm8000ControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                extronVolumeControlControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                initialSpotlight: {
                    $trueDescription: 'initial spotlight enabled',
                    $falseDescription: 'initial spotlight disabled',
                },
                ir: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('IR device settings changed');
                    },
                },
                isPowerToggle: {
                    $trueDescription: 'power toggle enabled',
                    $falseDescription: 'power on/off enabled',
                },
                lencoreControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                lightingControls: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('lighting controls changed');
                    },
                },
                lightingPresets: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('lighting presets changed');
                    },
                },
                macAddress: { $displayName: 'MAC address' },
                mediaOperations: {
                    channelTunerEnabled: {
                        $trueDescription: 'channel tuner enabled',
                        $falseDescription: 'channel tuner disabled',
                    },
                },
                name: { $displayName: 'configuration name' },
                nexiaControlChannels: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('channel configuration changed');
                    },
                },
                p300ControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                port: { $displayName: 'port' },
                presenceEnabled: {
                    $trueDescription: 'presence enabled',
                    $falseDescription: 'presence disabled',
                },
                qSysControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                radiusControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                soundStructureControlChannels: {
                    autoDetect: {
                        $trueDescription: 'auto detect enabled',
                        $falseDescription: 'auto detect disabled',
                    },
                    current: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                    detected: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('detected channels changed');
                        },
                    },
                },
                stewartAudioControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },

                synchronizedCalls: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('synchronized call settings changed');
                    },
                },
                tesiraControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
                useCalendar: {
                    $trueDescription: 'equipment calendar enabled',
                    $falseDescription: 'equipment calendar disabled',
                },
                useDirectory: {
                    $trueDescription: 'equipment directory enabled',
                    $falseDescription: 'equipment directory disabled',
                },
                videoInputs: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('video inputs configuration changed');
                    },
                },
                viscaControlChannels: {
                    cameras: {
                        manual: {
                            $displayFunc: function (desc, base, changed, format) {
                                desc.push('device configuration changed');
                            },
                        },
                    },
                },
                zoomConnectorEnabled: {
                    $trueDescription: 'Zoom Connector support enabled',
                    $falseDescription: 'Zoom Connector support disabled',
                },
                zoomControlChannels: {
                    manual: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('channel configuration changed');
                        },
                    },
                },
            },
            finalConnectState: {
                $displayFunc: function (desc, base, changed, format) {
                    if (changed === 'online') {
                        desc.push('is online');
                    } else if (changed === 'connectFailed') {
                        desc.push('is offline: connection failed');
                    } else if (changed === 'authFailed') {
                        desc.push('is offline: authentication failed');
                    } else if (changed === 'controlFailed') {
                        desc.push('is offline: invalid configuration');
                    } else {
                        desc.push('is offline');
                    }
                },
            },
            firmwareVersion: { $displayName: 'firmware version' },
            ipAddress: {
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('IP address changed to ');

                    if (typeof changed === 'undefined' || changed === null || changed === '') {
                        desc.push('no IP address');
                    } else {
                        desc.push(escape(changed, format));
                    }
                },
            },
            modelNumber: {
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('model number changed to ');
                    addModelNumber(desc, changed, format);
                },
            },
            password: {
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('password changed');
                },
            },
            serialNumber: { $displayName: 'serial number' },
            simulate: {
                $trueDescription: 'simulation enabled',
                $falseDescription: 'simulation disabled',
            },
            type: {
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('type changed to ');
                    addEquipmentType(desc, changed, format);
                },
            },
        },
        created: function (desc, event, format) {
            desc.push(' added to ');
            addRoomName(desc, event, false, format);
        },
        deleted: function (desc, event, format) {
            desc.push(' removed from ');
            addRoomName(desc, event, false, format);
        },
    },
    remoteControl: {
        $addName: function (desc, event, format) {
            desc.push('Remote control session');
        },
        auth: function (desc, event, format) {
            if (event.result === 'success') {
                desc.push('started ');
            } else {
                desc.push('denied ');
            }

            if (event.hasOwnProperty('userId')) {
                desc.push('for ');

                if (event.userId === 'anonymousRemoteControl') {
                    desc.push('anonymous user');
                } else {
                    addGenericUserName(
                        desc,
                        event.userFirstName,
                        event.userLastName,
                        event.userEmail,
                        format
                    );
                }

                var uaString = getUserAgent(event.userAgent);
                if (uaString !== null) {
                    desc.push(' (' + uaString + ' at ' + event.ipAddress + ')');
                } else {
                    desc.push('(' + event.ipAddress + ')');
                }
            }

            if (event.result === 'invalidCode') {
                desc.push(' because code was invalid');
            } else if (event.result === 'noCode') {
                desc.push(' because no code was provided');
            } else if (event.result === 'remoteControlDisabled') {
                desc.push(' because remote control is disabled');
            } else if (event.result !== 'success') {
                desc.push(' due to unknown error');
            }

            if (event.roomId) {
                desc.push(' in ');
                addNodeName(desc, event, format);
            }
        },
        deleted: function (desc, event, format) {
            if (event.result === 'success') {
                desc.push(' revoked ');
            } else {
                desc.push(' revocation failed ');
            }

            if (event.hasOwnProperty('userId')) {
                desc.push('for ');

                if (event.userId === 'anonymousRemoteControl') {
                    desc.push('anonymous user');
                } else {
                    addGenericUserName(
                        desc,
                        event.userFirstName,
                        event.userLastName,
                        event.userEmail,
                        format
                    );
                }

                var uaString = getUserAgent(event.userAgent);
                if (uaString !== null) {
                    if (event.ipAddress) {
                        desc.push(' (' + uaString + ' at ' + event.ipAddress + ')');
                    } else {
                        desc.push(' (' + uaString + ')');
                    }
                } else {
                    if (event.ipAddress) desc.push('(' + event.ipAddress + ')');
                }
            }

            if (event.result === 'notAuthenticated') {
                desc.push(" because request isn't from an authenticated user");
            } else if (event.result === 'noSessionId') {
                desc.push(' because no session ID was provided');
            } else if (event.result === 'invalidSessionId') {
                desc.push(' because an invalid session ID was provided');
            } else if (event.result === 'sessionIdNotInRoom') {
                desc.push(' because the sessionId is not valid for the specified room');
            } else if (event.result === 'invalidController') {
                desc.push(' because the request is from an unrecognized Mira Connect');
            } else if (event.result === 'controllerNotInRoom') {
                desc.push(' because the Mira Connect is not in the same room as the session');
            } else if (event.result === 'invalidUserId') {
                desc.push(' because the userId is not valid');
            } else if (event.result === 'noPermissionsForUser') {
                desc.push(" because the actor doesn't have permissions on the room");
            } else if (event.result !== 'success') {
                desc.push(' because of an unknown error');
            }

            if (event.roomId) {
                desc.push(' in ');
                addNodeName(desc, event, format);
            }
        },
    },
    role: {
        $addName: function (desc, event, format) {
            var roles = {
                admin: 'Administrator permissions',
                doer: 'Technical permissions',
                helper: 'Help Desk permissions',
                viewer: 'Reporting permissions',
            };

            desc.push(roles[event.role]);
        },
        changed: function (desc, event, format) {
            var roles = {
                admin: 'Administrator',
                doer: 'Technical',
                helper: 'Help Desk',
                viewer: 'Reporting',
            };

            desc.push('changed to ');
            desc.push(roles[event.changed.role]);
            desc.push(' for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        created: function (desc, event, format) {
            desc.push('added for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
        deleted: function (desc, event, format) {
            desc.push('deleted for ');
            addGenericUserName(
                desc,
                event.userFirstName,
                event.userLastName,
                event.userEmail,
                format
            );
            desc.push(' at ');
            addNodeName(desc, event, format);
        },
    },
    room: {
        $addName: function (desc, event, format) {
            addRoomName(desc, event, true, format);
        },
        changed: {
            name: {
                $altProp: 'roomName',
                $displayFunc: function (desc, prev, changed, format) {
                    desc.push('renamed to ');
                    desc.push(escape(changed, format));
                },
            },
            settings: {
                contacts: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('dial-in numbers changed')
                    },
                },
                o365CalendarEnabled: {
                    $trueDescription: 'Office 365 calendar enabled',
                    $falseDescription: 'Office 365 calendar disabled',
                },
                o365User: { $displayName: 'Office 365 user' },
                office365User: { $displayName: 'Office 365 user' },
                roomControlApiTcpSocketEnabled: {
                    $trueDescription: 'Room Control API telnet port enabled',
                    $falseDescription: 'Room Control API telnet port disabled',
                },
                roomControlApiWsEnabled: {
                    $trueDescription: 'Room Control API websocket port enabled',
                    $falseDescription: 'Room Control API websocket port disabled',
                },
                roomControlApiWssEnabled: {
                    $trueDescription: 'Room Control API secure websocket port enabled',
                    $falseDescription: 'Room Control API secure websocket port disabled',
                },
                userPassword: { $displayName: 'user password' },
                userPasswordTimeoutSec: { $displayName: 'user password timeout (seconds)' },
                whoCanHearMeIcon: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('Who Can Hear Me icon changed');
                    },
                },
                whoCanHearMeLabel: { $displayName: 'Who Can Hear Me label' },
            },
            states: {
                // Both the 'online' and 'onlineReported' properties are
                // deprecated; the 'onlineState' property is now used instead.
                // We keep the 'online' and 'onlineReported' definitions in the
                // event formatter to handle old events that were generated
                // before onlineState was added. Eventually we should convert
                // the old events in the database and then remove these old
                // definitions.
                online: {
                    // deprecated
                    $trueDescription: 'is online',
                    $falseDescription: 'is offline',
                },
                onlineReported: {
                    // deprecated
                    $trueDescription: 'is online',
                    $falseDescription: 'is offline',
                },
                onlineState: {
                    $displayFunc: function (desc, base, changed, format) {
                        if (changed === 'online') {
                            desc.push('is online');
                        } else if (changed === 'limited') {
                            desc.push(
                                'has limited functionality because ' + 'some equipment is offline'
                            );
                        } else {
                            desc.push('is offline');
                        }
                    },
                },
            },
        },
        created: 'created',
        deleted: 'deleted',
    },
    roomApiKey: {
        $addName: function (desc, event, format) {
            desc.push('API Key ');
            if (event.description) desc.push('"' + event.description + '" ');
            desc.push('(' + event.apiKey + ') ');
            desc.push(event.action === 'created' ? 'added' : 'removed');
            desc.push(' in ');
            addRoomName(desc, event, true, format);
        },
        created: function (desc, event, format) {},
        deleted: function (desc, event, format) {},
    },
    site: {
        $addName: function (desc, event, format) {
            addSiteName(desc, event, true, format);
        },
        changed: {
            name: {
                $altProp: 'siteName',
                $displayFunc: function (desc, prev, changed, format) {
                    desc.push('renamed to ');
                    desc.push(escape(changed, format));
                },
            },
            address: { $displayName: 'address' },
            settings: {
                appAutoUpdateTime: { $displayName: 'application auto update time' },
                defaultCallTypes: {
                    audio: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('default audio call types changed to ');
                            if (changed === undefined || changed === null || changed.length === 0) {
                                desc.push('none');
                            } else {
                                desc.push(changed.join(', '));
                            }
                        },
                    },
                    video: {
                        $displayFunc: function (desc, base, changed, format) {
                            desc.push('default video call types changed to ');
                            if (changed === undefined || changed === null || changed.length === 0) {
                                desc.push('none');
                            } else {
                                desc.push(changed.join(', '));
                            }
                        },
                    },
                },
                idleEnabled: {
                    $trueDescription: 'idle timeout enabled',
                    $falseDescription: 'idle timeout disabled',
                },
                idleScreen: { $displayName: 'idle screen' },
                idleTimeout: { $displayName: 'idle timeout' },
                inaraChannel: { $displayName: defaults.inara.name + ' software channel' },
                micsMuteBehaviorOnRoomPowerOff: {
                    $displayName: 'mics behavior on room power off',
                },
                recentCallsEnabled: {
                    $trueDescription: 'recent calls enabled',
                    $falseDescription: 'recent calls disabled',
                },
                remoteControlEnabled: {
                    $trueDescription: 'remote control enabled',
                    $falseDescription: 'remote control disabled',
                },
                remoteControlSessionDurationMinutes: {
                    $displayName: 'remote control session duration (minutes)',
                },
                roomPowerOffEnabled: {
                    $trueDescription: 'room power off option enabled',
                    $falseDescription: 'room power off option disabled',
                },
                roomPowerOffSchedule: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('room power off schedule changed');
                    },
                },
                sleepEnabled: {
                    $trueDescription: 'sleep enabled',
                    $falseDescription: 'sleep disabled',
                },
                sleepTimeout: { $displayName: 'smart sleep timeout' },
                synergySkyEnabled: {
                    $trueDescription: 'Synergy Sky integration enabled',
                    $falseDescription: 'Synergy Sky integration disabled',
                },
                synergySkyDomain: { $displayName: 'Synergy Sky domain' },
                synergySkyCallTypes: {
                    $displayFunc: function (desc, base, changed, format) {
                        desc.push('Synergy Sky call types changed to ');
                        var keys = changed === null || changed === undefined
                            ? [] : Object.keys(changed);

                        if (keys.length <= 0) {
                            desc.push('none');
                        } else {
                            desc.push(keys.join(', '));
                        }
                    },
                },
                timeZone: { $displayName: 'time zone' },
            },
        },
        created: 'created',
        deleted: 'deleted',
    },
    user: {
        $addName: function (desc, event, format) {
            addUserName(desc, event, true, format);
        },
        changed: {
            email: { $displayName: 'email' },
            firstName: { $displayName: 'first name' },
            lastName: { $displayName: 'last name' },
            lastLogin: {
                $displayFunc: function (desc, base, changed, format) {
                    desc.push('logged in');
                },
            },
            mobileNumber: { $displayName: 'mobile number' },
        },
        created: 'created',
        deleted: 'deleted',
        passwordChanged: 'password changed',
        passwordReset: 'password reset',
    },
};

function addCompanyName(desc, event, includePrefix, format) {
    if (includePrefix) desc.push('Organization ');
    addNodeLink(desc, 'company', event.companyName, event.companyId, format);
}

function addControllerName(desc, event, includePrefix, format, formatOptions) {
    if (includePrefix) desc.push('Controller ');
    addNodeLink(
        desc,
        'controller',
        event.serialNumber,
        event.controllerId,
        typeof formatOptions !== 'undefined' &&
            formatOptions !== null &&
            typeof formatOptions.controllerId !== 'undefined'
            ? formatOptions.controllerId
            : format
    );

    if (event.hasOwnProperty('roomId')) {
        desc.push(' in ');
        addRoomName(desc, event, false, format);
    }
}

function addEquipmentName(desc, event, includePrefix, format) {
    if (includePrefix) desc.push('Equipment ');

    addEquipmentType(desc, event.type, event.modelNumber, format);
    desc.push(' (');

    if (
        typeof event.ipAddress === 'undefined' ||
        event.ipAddress === null ||
        event.ipAddress === ''
    ) {
        desc.push('no IP address');
    } else {
        desc.push(escape(event.ipAddress, format));
    }

    desc.push(')');
}

function addEquipmentType(desc, type, formatOrModelNumber, format) {
    var modelNumber;

    if (typeof format === 'undefined') {
        format = formatOrModelNumber;
    } else {
        modelNumber = formatOrModelNumber;
    }

    var eqTypes = KayleeEquipmentTypes.getEquipmentTypes();

    if (eqTypes.$isLoaded()) {
        var eqType = eqTypes.$recordAt(type);
        var transport;
        if (eqType !== null && eqType.hasOwnProperty('driverVariant')) {
            transport = eqType.driverVariant.type;
            eqType = eqTypes.$recordAt(eqType.driverVariant.parentEquipmentId);
        }
        if (eqType !== null) {
            desc.push(escape(eqType.manufacturer, format));
            desc.push(' ');
            desc.push(escape(eqType.model, format));
        } else {
            desc.push(escape('<unknown>', format));
            return;
        }

        if (typeof modelNumber !== 'undefined') {
            desc.push(' ');
            desc.push(escape(eqTypes.$recordAt(type).modelNumbers[modelNumber], format));
        }

        if (typeof transport !== 'undefined') {
            desc.push(escape(' (' + transport + ')'));
        }
    } else {
        desc.push(escape('<unknown>', format));
    }
}

function addModelNumber(desc, modelNumber, format) {
    var eqTypes = KayleeEquipmentTypes.getEquipmentTypes();
    var eqType = null;

    // We don't have access to the equipment type, so we have to iterate through
    // all of them to try and find the one with this model number.

    if (eqTypes.$isLoaded()) {
        for (var i = 0; i < eqTypes.length; i++) {
            if (
                eqTypes[i].hasOwnProperty('modelNumbers') &&
                eqTypes[i].modelNumbers.hasOwnProperty(modelNumber)
            ) {
                eqType = eqTypes[i];
                break;
            }
        }

        if (eqType === null) {
            log.error('could not find equipment type for model number id ' + modelNumber);
            return;
        }

        desc.push(escape(eqType.modelNumbers[modelNumber], format));
    } else {
        desc.push(escape('<unknown>', format));
    }
}

function addGenericUserName(desc, firstName, lastName, email, format) {
    desc.push(escape(firstName, format));
    desc.push(' ');
    desc.push(escape(lastName, format));
    desc.push(escape(' <', format));
    desc.push(email);
    desc.push(escape('>', format));
}

function addNodeLink(desc, type, name, id, format) {
    if (format === 'html') {
        desc.push('<a href="/#!/portal/');
        desc.push(type);
        desc.push('/');
        desc.push(id);
        desc.push('">');
        desc.push(escape(name, format));
        desc.push('</a>');
    } else if (format === 'slack') {
        desc.push('<');

        desc.push('https://');
        desc.push(KayleeBase.config[KayleeBase.alias].authDomain);
        desc.push('/#!/portal/');
        desc.push(type);
        desc.push('/');
        desc.push(id);
        desc.push('|');
        desc.push(escape(name, format));
        desc.push('>');
    } else {
        desc.push(name);
    }
}

function addNodeName(desc, event, format) {
    if (event.hasOwnProperty('billingAccountId') || event.hasOwnProperty('distributorId')) {
        desc.push(event.name);
        return;
    }

    if (!event.hasOwnProperty('companyId')) return;

    addNodeLink(desc, 'company', event.companyName, event.companyId, format);

    if (event.hasOwnProperty('siteId')) {
        desc.push(escape(' > ', format));
        addNodeLink(desc, 'site', event.siteName, event.siteId, format);

        if (event.hasOwnProperty('roomId')) {
            desc.push(escape(' > ', format));
            addNodeLink(desc, 'room', event.roomName, event.roomId, format);
        }
    }
}

function addRoomName(desc, event, includePrefix, format) {
    if (includePrefix) desc.push('Room ');
    addNodeLink(desc, 'company', event.companyName, event.companyId, format);
    desc.push(escape(' > ', format));
    addNodeLink(desc, 'site', event.siteName, event.siteId, format);
    desc.push(escape(' > ', format));
    addNodeLink(desc, 'room', event.roomName, event.roomId, format);
}

function addSiteName(desc, event, includePrefix, format) {
    if (includePrefix) desc.push('Site ');
    addNodeLink(desc, 'company', event.companyName, event.companyId, format);
    desc.push(escape(' > ', format));
    addNodeLink(desc, 'site', event.siteName, event.siteId, format);
}

function addUserName(desc, event, includePrefix, format) {
    if (includePrefix) desc.push('User ');

    if (event.hasOwnProperty('firstName')) {
        addGenericUserName(desc, event.firstName, event.lastName, event.email, format);
    } else {
        desc.push('Unknown (user may be deleted)');
    }
}

function escape(str, format) {
    if (typeof str !== 'string') str = str.toString();
    if (format === 'html') {
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    return str;
}

function getActor(event, format) {
    if (event.hasOwnProperty('actorUserId')) {
        if (event.actorUserId === 'anonymousRemoteControl') {
            return 'anonymous user';
        } else if (KayleeBase.isInaraUserId(event.actorUserId)) {
            return 'Mira Connect';
        }

        var desc = [];

        addGenericUserName(
            desc,
            event.actorFirstName,
            event.actorLastName,
            event.actorEmail,
            format
        );

        return desc.join('');
    }

    return null;
}

function addChangedDescription(desc, base, changed, info, format, n) {
    if (typeof n === 'undefined') n = 0;

    for (var prop in changed) {
        if (!changed.hasOwnProperty(prop)) continue;

        if (info.hasOwnProperty(prop)) {
            var baseProp;
            var changedProp = changed[prop];
            var infoProp = info[prop];

            if (typeof base === 'object') baseProp = base[prop];

            if (info.hasOwnProperty('$altProp')) {
                baseProp = base[infoProp.$altProp];
            }

            if (infoProp.hasOwnProperty('$displayName')) {
                if (n > 0) desc.push(', ');
                n++;
                desc.push(infoProp.$displayName);

                if (typeof baseProp !== 'undefined') {
                    desc.push(' changed from ');
                    desc.push(escape(baseProp, format));
                    desc.push(' to ');
                } else {
                    desc.push(' changed to ');
                }

                if (changedProp.hasOwnProperty('removed') && changedProp.removed === true) {
                    desc.push('&lt;none&gt;')
                } else {
                    desc.push(escape(changedProp, format));
                }
            } else if (infoProp.hasOwnProperty('$trueDescription')) {
                if (n > 0) desc.push(', ');
                n++;
                if (changedProp) {
                    desc.push(infoProp.$trueDescription);
                } else {
                    desc.push(infoProp.$falseDescription);
                }
            } else if (infoProp.hasOwnProperty('$displayFunc')) {
                if (n > 0) desc.push(', ');
                n++;
                infoProp.$displayFunc(desc, baseProp, changedProp, format);
            } else if (typeof changedProp === 'object') {
                n = addChangedDescription(desc, baseProp, changedProp, infoProp, format, n);
            } else {
                log.error("couldn't find a way to format " + prop);
            }
        }
    }

    return n;
}

function getDescription(event, format, formatOptions) {
    var desc = [];

    if (event.hasOwnProperty('$isLoaded') && !event.$isLoaded()) {
        log.debug('event ' + event.$id + ' not loaded yet');
        return '';
    }

    if (event.hasOwnProperty('object') && formatInfo.hasOwnProperty(event.object)) {
        var fio = formatInfo[event.object];

        if (event.hasOwnProperty('action') && fio.hasOwnProperty(event.action)) {
            if (event.action === 'changed') {
                fio.$addName(desc, event, format, formatOptions);
                desc.push(' ');

                if (typeof fio.changed === 'function') {
                    fio.changed(desc, event, format);
                } else {
                    addChangedDescription(desc, event, event.changed, fio.changed, format);
                }
            } else {
                fio.$addName(desc, event, format, formatOptions);
                desc.push(' ');
                if (typeof fio[event.action] === 'function') {
                    fio[event.action](desc, event, format);
                } else {
                    desc.push(fio[event.action]);
                }
            }
        }
    }

    if (desc.length === 0) {
        log.error("couldn't format event");
        desc.push('Unknown');
    } else {
        if (formatOptions && formatOptions.addNodeName) {
            desc.push(' in ');
            addNodeName(desc, event, format);
        }
    }

    return desc.join('');
}

function getUserAgent(userAgentString) {
    if (userAgentString === null || typeof userAgentString === 'undefined') return null;

    uaParser.setUA(userAgentString);

    var device = uaParser.getDevice();

    if (device.vendor || device.model) {
        return (device.vendor ? device.vendor + ' ' : '') + (device.model ? device.model : '');
    } else {
        return uaParser.getOS().name + ' ' + uaParser.getBrowser().name;
    }
}

module.exports = KayleeEventFormatter;
